import RestServices from '~/api';
import { KEY_SESSION_STORAGE } from '~/constants';
import generateUUID from '~/helpers/generateUUID';
import { getFromLocalStorage, saveToLocalStorage } from '~/helpers/storage';
import { clickstreamLogger } from '~/logging';
import settings from '~/utils/Settings';

/**
 * Флаг создания новой сессии. true - новая сессия
 */
let isNewSession = false;

interface ISessionData {
  sessionId: string;
  timestamp: number;
}

/**
 * Обновляет временную метку сессии
 */
const updateSessionTimestamp = (sessionId: string): ISessionData | false => {
  if (sessionId) {
    const updatedSessionDate = {
      sessionId,
      timestamp: Date.now(),
    };
    clickstreamLogger.info(
      `Сессия обновлена ${updatedSessionDate.sessionId} - ${new Date(updatedSessionDate.timestamp)}`
    );

    return updatedSessionDate;
  }
  clickstreamLogger.error(
    `Ошибка обновления временной метки сессии ${sessionId} - ${new Date()}`
  );
  
  return false;
};

/**
 * Отправляет запрос NewSession и меняет флаг isNewSession
 */
const sendInfoCreatedSession = () => {
  RestServices.newSession.set()
    .then(() => isNewSession = false);
};

/**
 * Создает новую сессию
 * @returns SessionData
 */
const createNewSession = (): ISessionData => {
  const sessionData = {
    sessionId: generateUUID(),
    timestamp: Date.now(),
  };
  clickstreamLogger.info(
    `Создана новая сессия: ${sessionData.sessionId} - ${new Date(sessionData.timestamp)}`
  );
  isNewSession = true;

  return sessionData;
};

/**
 * Проверяет временную метку сессии 
 * Возвращает true если сессия просрочена
 * @param timestamp 
 * @returns 
 */
const hasSessionExpired = (timestamp: number): boolean => {
  const SESSION_EXPIRED_TIME_MS = settings.getOrDefault('SESSION_EXPIRED_TIME_MS', 1800000);
  const currentTimestamp = Date.now();
  if (!timestamp || currentTimestamp - timestamp > SESSION_EXPIRED_TIME_MS) return true;

  return false;
};

/**
 * Обновляет значении сессии в декораторе запросов
 * @param sessionId 
 */
const setSessionInDecorator = (sessionId: string) => {
  RestServices.setParameters({
    sessionId,
  });
};

/**
 * Возвращает актуальные данные сессии
 * @returns SessionData
 */
const getSessionData = (): ISessionData => {
  const sessionData = getFromLocalStorage<ISessionData>(KEY_SESSION_STORAGE, 'sessionData');
  if (sessionData && !hasSessionExpired(sessionData.timestamp)) {
    const updatedSessionData = updateSessionTimestamp(sessionData.sessionId);
    if (updatedSessionData) return updatedSessionData;
  }

  return createNewSession();
};

/**
 * Запускает интервал выполнения обновлений временной метки сессии
 */
const startIntervalSessionUpdate = () => {
  const SESSION_UPDATE_INTERVAL_MS = settings.getOrDefault('SESSION_UPDATE_INTERVAL_MS', 300000);
  setInterval(() => {
    if (document.visibilityState === 'visible') {
      startControlSession();

      return;
    }
    clickstreamLogger.info(
      `Сессия не обновлена - страница не активна`
    );   
  }, SESSION_UPDATE_INTERVAL_MS);
};

/**
 * Контролирует актуальность сессии
 */
const startControlSession = () => {
  const sessionData = getSessionData();
  saveToLocalStorage(KEY_SESSION_STORAGE, 'sessionData', sessionData);
  setSessionInDecorator(sessionData.sessionId);
  if (isNewSession) sendInfoCreatedSession();
};

/**
 * Определяет актуальную сессию и добавляет ее в декоратор запросов
 * @returns 
 */
export const initSessionController = () => {
  startControlSession();
  startIntervalSessionUpdate();
};
